import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Key,
  FileText,
  Database,
  LogOut,
  Search,
  BarChart3,
  HardDrive,
  TrendingUp,
  Settings,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { App, ApiKey, ApiKeyWithKey, ApiKeyPermission, SystemStats, LogEntry, SearchParams, ExportFormat, StatsSummary, TimelineResponse, AggregationConfig, AggregationConfigInput, AggregationFilters, AggregationResult } from '../lib/api.js';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { LoginForm } from './LoginForm.js';
import { AppManager } from './AppManager.js';
import { ApiKeyManager } from './ApiKeyManager.js';
import { LogViewer } from './LogViewer.js';
import { Stats } from './Stats.js';
import { AggregationConfigManager } from './AggregationConfig.js';

type TabType = 'overview' | 'stats' | 'logs' | 'apps' | 'api-keys' | 'aggregations';

const TAB_ROUTES: Record<TabType, string> = {
  'overview': '/',
  'stats': '/stats',
  'logs': '/logs',
  'apps': '/apps',
  'api-keys': '/api-keys',
  'aggregations': '/aggregations',
};

const ROUTE_TO_TAB: Record<string, TabType> = {
  '/': 'overview',
  '/stats': 'stats',
  '/logs': 'logs',
  '/apps': 'apps',
  '/api-keys': 'api-keys',
  '/aggregations': 'aggregations',
};

export interface DashboardProps {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Callback to handle login */
  onLogin: (password: string) => Promise<void>;
  /** Callback to handle logout */
  onLogout: () => Promise<void>;
  /** Login error message */
  loginError?: string | null;
  /** Whether login is in progress */
  isLoggingIn?: boolean;

  /** System statistics */
  stats?: SystemStats | null;
  /** Whether stats are loading */
  isLoadingStats?: boolean;

  /** Array of apps */
  apps: App[];
  /** Callback when an app is created */
  onCreateApp: (name: string) => Promise<void>;
  /** Callback when an app is deleted */
  onDeleteApp: (appId: string) => Promise<void>;
  /** Whether apps are loading */
  isLoadingApps?: boolean;
  /** Apps error message */
  appsError?: string | null;

  /** Array of API keys */
  apiKeys: ApiKey[];
  /** Callback when an API key is created */
  onCreateApiKey: (appId: string, permissions: ApiKeyPermission, rateLimit: number) => Promise<ApiKeyWithKey>;
  /** Callback when an API key is deleted */
  onDeleteApiKey: (keyId: string) => Promise<void>;
  /** Whether API keys are loading */
  isLoadingApiKeys?: boolean;
  /** API keys error message */
  apiKeysError?: string | null;

  /** Log entries for the log viewer */
  logs: LogEntry[];
  /** Total logs matching search */
  logsTotal: number;
  /** Whether there are more logs */
  logsHasMore: boolean;
  /** Callback when logs are searched */
  onSearchLogs: (params: SearchParams, appId: string) => void;
  /** Whether logs are loading */
  isLoadingLogs?: boolean;
  /** Logs error message */
  logsError?: string | null;

  /** Current page for logs */
  logsPage?: number;
  /** Page size for logs */
  logsPageSize?: number;
  /** Callback when log page changes */
  onLogsPageChange?: (page: number) => void;
  /** Callback when log page size changes */
  onLogsPageSizeChange?: (pageSize: number) => void;

  /** Callback when logs are exported */
  onExportLogs?: (appId: string, format: ExportFormat, params: SearchParams) => Promise<void>;
  /** Callback when logs are cleared */
  onClearLogs?: (appId: string, params?: SearchParams) => Promise<void>;

  /** App stats summary */
  appStatsSummary?: StatsSummary | null;
  /** App stats timeline */
  appStatsTimeline?: TimelineResponse | null;
  /** Whether app stats are loading */
  isLoadingAppStats?: boolean;
  /** App stats error */
  appStatsError?: string | null;
  /** Stats time range */
  statsTimeRange?: { from: string; to: string };
  /** Callback when stats time range changes */
  onStatsTimeRangeChange?: (from: string, to: string) => void;
  /** Callback when stats app selection changes */
  onStatsAppIdChange?: (appId: string | null) => void;

  /** Aggregation configs */
  aggregationConfigs?: AggregationConfig[];
  /** Whether aggregation configs are loading */
  isLoadingAggregations?: boolean;
  /** Aggregation configs error */
  aggregationsError?: string | null;
  /** Callback when aggregation is created */
  onCreateAggregation?: (appId: string, config: AggregationConfigInput) => Promise<void>;
  /** Callback when aggregation is updated */
  onUpdateAggregation?: (appId: string, configId: string, config: Partial<AggregationConfigInput>) => Promise<void>;
  /** Callback when aggregation is deleted */
  onDeleteAggregation?: (appId: string, configId: string) => Promise<void>;
  /** Callback when aggregation is computed */
  onComputeAggregation?: (appId: string, configId: string, from: string, to: string, filters?: AggregationFilters | null) => Promise<AggregationResult[]>;
  /** Callback when aggregation app selection changes */
  onAggregationAppIdChange?: (appId: string | null) => void;

  /** Additional class name */
  className?: string;
}

/**
 * Dashboard is the main admin interface for the log collector.
 */
export function Dashboard({
  isAuthenticated,
  onLogin,
  onLogout,
  loginError,
  isLoggingIn = false,

  stats,
  isLoadingStats = false,

  apps,
  onCreateApp,
  onDeleteApp,
  isLoadingApps = false,
  appsError,

  apiKeys,
  onCreateApiKey,
  onDeleteApiKey,
  isLoadingApiKeys = false,
  apiKeysError,

  logs,
  logsTotal,
  logsHasMore,
  onSearchLogs,
  isLoadingLogs = false,
  logsError,

  logsPage = 1,
  logsPageSize = 100,
  onLogsPageChange,
  onLogsPageSizeChange,

  onExportLogs,
  onClearLogs,

  appStatsSummary,
  appStatsTimeline,
  isLoadingAppStats = false,
  appStatsError,
  statsTimeRange,
  onStatsTimeRangeChange,
  onStatsAppIdChange,

  aggregationConfigs = [],
  isLoadingAggregations = false,
  aggregationsError,
  onCreateAggregation,
  onUpdateAggregation,
  onDeleteAggregation,
  onComputeAggregation,
  onAggregationAppIdChange,

  className,
}: DashboardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAppId, setSelectedAppId] = React.useState<string | null>(null);

  // Derive active tab from URL
  const activeTab: TabType = ROUTE_TO_TAB[location.pathname] || 'overview';

  // Navigate to a tab
  const setActiveTab = React.useCallback((tab: TabType) => {
    navigate(TAB_ROUTES[tab]);
  }, [navigate]);

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <LoginForm
        onLogin={onLogin}
        error={loginError}
        isLoading={isLoggingIn}
        className={className}
      />
    );
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const tabs: Array<{ id: TabType; label: string; icon: React.ElementType }> = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'stats', label: 'Stats', icon: TrendingUp },
    { id: 'logs', label: 'Logs', icon: Search },
    { id: 'apps', label: 'Apps', icon: Box },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'aggregations', label: 'Aggregations', icon: Settings },
  ];

  return (
    <div
      className={cn(
        'relative flex min-h-screen flex-col',
        'bg-[var(--color-background)]',
        className
      )}
    >
      {/* Subtle background gradient */}
      <div
        className="pointer-events-none fixed inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="absolute -left-1/3 -top-1/4 h-[800px] w-[800px] rounded-full opacity-[0.03]"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.8) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute -bottom-1/4 -right-1/4 h-[600px] w-[600px] rounded-full opacity-[0.03]"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.8) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Header */}
      <header
        className={cn(
          'sticky top-0 z-40',
          'border-b border-[var(--color-border)]',
          'bg-[var(--color-background)]/80 backdrop-blur-xl',
          'shadow-[0_1px_3px_rgba(0,0,0,0.2)]'
        )}
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 shadow-[0_0_12px_rgba(59,130,246,0.15)]">
              <FileText className="h-4 w-4 text-[var(--color-primary)]" />
            </div>
            <span className="text-lg font-semibold tracking-tight text-[var(--color-foreground)]">
              Log Collector
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
        {/* Tab Navigation */}
        <nav
          className={cn(
            'mb-6 flex space-x-1 rounded-xl p-1',
            'bg-[var(--color-secondary)]/50',
            'border border-[var(--color-border)]',
            'shadow-[0_1px_3px_rgba(0,0,0,0.1)]'
          )}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium',
                  'transition-all duration-200 ease-out',
                  isActive
                    ? [
                        'bg-[var(--color-card)] text-[var(--color-foreground)]',
                        'shadow-[0_1px_3px_rgba(0,0,0,0.2),0_1px_2px_rgba(0,0,0,0.1)]',
                        'border border-[var(--color-border)]',
                      ].join(' ')
                    : [
                        'text-[var(--color-muted-foreground)]',
                        'hover:text-[var(--color-foreground)]',
                        'hover:bg-[var(--color-muted)]/30',
                      ].join(' ')
                )}
              >
                <Icon className={cn('h-4 w-4', isActive && 'text-[var(--color-primary)]')} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-3">
              {/* Total Apps Card */}
              <Card
                className={cn(
                  'group relative overflow-hidden',
                  'hover:shadow-[0_4px_20px_rgba(59,130,246,0.1)]',
                  'transition-all duration-300'
                )}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background: 'radial-gradient(circle at top right, rgba(59, 130, 246, 0.08) 0%, transparent 60%)',
                  }}
                />
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">
                    Total Apps
                  </CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
                    <Box className="h-4 w-4 text-[var(--color-primary)]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold tracking-tight text-[var(--color-foreground)]">
                    {isLoadingStats ? (
                      <div className="h-9 w-16 animate-pulse rounded bg-[var(--color-muted)]" />
                    ) : (
                      stats?.apps_count ?? 0
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Applications registered
                  </p>
                </CardContent>
              </Card>

              {/* Total API Keys Card */}
              <Card
                className={cn(
                  'group relative overflow-hidden',
                  'hover:shadow-[0_4px_20px_rgba(139,92,246,0.1)]',
                  'transition-all duration-300'
                )}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background: 'radial-gradient(circle at top right, rgba(139, 92, 246, 0.08) 0%, transparent 60%)',
                  }}
                />
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">
                    Total API Keys
                  </CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10">
                    <Key className="h-4 w-4 text-purple-400" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold tracking-tight text-[var(--color-foreground)]">
                    {isLoadingStats ? (
                      <div className="h-9 w-16 animate-pulse rounded bg-[var(--color-muted)]" />
                    ) : (
                      stats?.api_keys_count ?? 0
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Active API keys
                  </p>
                </CardContent>
              </Card>

              {/* Total Logs Card */}
              <Card
                className={cn(
                  'group relative overflow-hidden',
                  'hover:shadow-[0_4px_20px_rgba(34,197,94,0.1)]',
                  'transition-all duration-300'
                )}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background: 'radial-gradient(circle at top right, rgba(34, 197, 94, 0.08) 0%, transparent 60%)',
                  }}
                />
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">
                    Total Logs
                  </CardTitle>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500/10">
                    <FileText className="h-4 w-4 text-green-400" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold tracking-tight text-[var(--color-foreground)]">
                    {isLoadingStats ? (
                      <div className="h-9 w-20 animate-pulse rounded bg-[var(--color-muted)]" />
                    ) : (
                      (stats?.total_logs ?? 0).toLocaleString()
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Log entries collected
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Per-App Stats */}
            {stats?.logs_by_app && stats.logs_by_app.length > 0 && (
              <Card className="overflow-hidden">
                <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-secondary)]/30">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary)]/10">
                      <Database className="h-5 w-5 text-[var(--color-primary)]" />
                    </div>
                    <div>
                      <CardTitle className="text-[var(--color-foreground)]">Logs by App</CardTitle>
                      <CardDescription className="text-[var(--color-muted-foreground)]">
                        Log count and database size for each application
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-[var(--color-border)]">
                    {stats.logs_by_app.map((appStats) => (
                      <div
                        key={appStats.app_id}
                        className={cn(
                          'flex items-center justify-between px-6 py-4',
                          'transition-colors duration-150',
                          'hover:bg-[var(--color-muted)]/20'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-secondary)]">
                            <Box className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                          </div>
                          <div>
                            <p className="font-medium text-[var(--color-foreground)]">
                              {appStats.app_name}
                            </p>
                            <p className="font-mono text-xs text-[var(--color-muted-foreground)]">
                              {appStats.app_id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-right">
                          <div>
                            <p className="text-lg font-semibold text-[var(--color-foreground)]">
                              {appStats.log_count.toLocaleString()}
                            </p>
                            <p className="text-xs text-[var(--color-muted-foreground)]">logs</p>
                          </div>
                          <div className="flex items-center gap-2 rounded-lg bg-[var(--color-secondary)] px-3 py-1.5">
                            <HardDrive className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
                            <span className="text-sm font-medium text-[var(--color-muted-foreground)]">
                              {formatBytes(appStats.db_size_bytes)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty state for no apps */}
            {(!stats?.logs_by_app || stats.logs_by_app.length === 0) && !isLoadingStats && (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-secondary)]">
                    <Box className="h-6 w-6 text-[var(--color-muted-foreground)]" />
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-[var(--color-foreground)]">
                    No apps yet
                  </h3>
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                    Create your first app to start collecting logs
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => setActiveTab('apps')}
                  >
                    <Box className="h-4 w-4" />
                    Create App
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <Stats
            apps={apps}
            selectedAppId={selectedAppId}
            onSelectApp={(appId) => {
              setSelectedAppId(appId);
              onStatsAppIdChange?.(appId);
            }}
            summary={appStatsSummary ?? null}
            timeline={appStatsTimeline ?? null}
            isLoading={isLoadingAppStats}
            error={appStatsError ?? null}
            from={statsTimeRange?.from ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()}
            to={statsTimeRange?.to ?? new Date().toISOString()}
            onTimeRangeChange={onStatsTimeRangeChange ?? (() => {})}
          />
        )}

        {activeTab === 'logs' && (
          <LogViewer
            apps={apps}
            selectedAppId={selectedAppId}
            onSelectApp={setSelectedAppId}
            logs={logs}
            total={logsTotal}
            hasMore={logsHasMore}
            page={logsPage}
            pageSize={logsPageSize}
            onSearch={onSearchLogs}
            onPageChange={onLogsPageChange}
            onPageSizeChange={onLogsPageSizeChange}
            onExport={onExportLogs}
            onClearLogs={onClearLogs}
            isLoading={isLoadingLogs}
            error={logsError}
          />
        )}

        {activeTab === 'apps' && (
          <AppManager
            apps={apps}
            onCreateApp={onCreateApp}
            onDeleteApp={onDeleteApp}
            onSelectApp={(app) => {
              setSelectedAppId(app.id);
              setActiveTab('api-keys');
            }}
            selectedAppId={selectedAppId}
            isLoading={isLoadingApps}
            error={appsError}
          />
        )}

        {activeTab === 'api-keys' && (
          <ApiKeyManager
            apiKeys={apiKeys}
            apps={apps}
            selectedAppId={selectedAppId}
            onSelectApp={setSelectedAppId}
            onCreateApiKey={onCreateApiKey}
            onDeleteApiKey={onDeleteApiKey}
            isLoading={isLoadingApiKeys}
            error={apiKeysError}
          />
        )}

        {activeTab === 'aggregations' && (
          <AggregationConfigManager
            apps={apps}
            selectedAppId={selectedAppId}
            onSelectApp={(appId) => {
              setSelectedAppId(appId);
              onAggregationAppIdChange?.(appId);
            }}
            configs={aggregationConfigs}
            isLoading={isLoadingAggregations}
            error={aggregationsError}
            onCreate={onCreateAggregation || (async () => {})}
            onUpdate={onUpdateAggregation || (async () => {})}
            onDelete={onDeleteAggregation || (async () => {})}
            onCompute={onComputeAggregation || (async () => [])}
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard;
